import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganizationsList } from '../../../hooks/useApiQueries';
import { useAdminCreateAgent, useAdminLeadsVoAgents, useAdminToggleEnabled } from '../../../hooks/useLeadsVoQueries';

const headerStyle = {
  background: '#262626',
  color: 'white',
  fontWeight: 'bold',
  fontSize: 14,
  position: 'sticky' as const,
  top: 0,
  zIndex: 100,
  whiteSpace: 'nowrap' as const,
};

const cellStyle = {
  height: 55,
  verticalAlign: 'middle' as const,
  fontSize: 14,
  color: '#262626',
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  onboarding: { bg: '#FFF3E0', color: '#E65100' },
  active: { bg: '#E8F5E9', color: '#2E7D32' },
  paused: { bg: '#FFF8E1', color: '#F57F17' },
  inactive: { bg: '#FFEBEE', color: '#C62828' },
};

const AdminAgents = () => {
  const { data: agents, isLoading } = useAdminLeadsVoAgents();
  const toggleEnabled = useAdminToggleEnabled();
  const createAgent = useAdminCreateAgent();

  const { data: organizations } = useOrganizationsList();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [error, setError] = useState('');

  const existingOrgIds = new Set(agents?.map((a) => a.organization_id) ?? []);
  const availableOrgs = (organizations ?? []).filter((org: any) => !existingOrgIds.has(org.id));

  const handleCreate = async () => {
    if (!selectedOrg) {
      setError('Sélectionnez une agence');
      return;
    }
    try {
      setError('');
      await createAgent.mutateAsync(selectedOrg.id);
      setDialogOpen(false);
      setSelectedOrg(null);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Erreur lors de la création');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626' }}>
            Agents RDV Qualifiés
          </Typography>
          <Chip
            label={`${agents?.length ?? 0} agents`}
            sx={{ backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 600 }}
          />
        </Box>
        <Button
          onClick={() => setDialogOpen(true)}
          sx={{
            backgroundColor: '#FDBB5F',
            color: '#262626',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 15,
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': { backgroundColor: '#fcae4d' },
          }}
        >
          Inviter une agence
        </Button>
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>ID</TableCell>
                <TableCell sx={headerStyle}>Agence</TableCell>
                <TableCell sx={headerStyle}>ID RDV Qualifiés</TableCell>
                <TableCell sx={headerStyle}>Statut</TableCell>
                <TableCell sx={headerStyle}>Calendrier</TableCell>
                <TableCell sx={headerStyle} align="center">RDV Qualifiés activé</TableCell>
                <TableCell sx={headerStyle}>Créé le</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents && agents.length > 0 ? (
                agents.map((agent) => {
                  const statusColor = STATUS_COLORS[agent.status] ?? { bg: '#F5F5F5', color: '#616161' };
                  return (
                    <TableRow key={agent.id} hover sx={{ '&:hover': { backgroundColor: '#FAFAFA' } }}>
                      <TableCell sx={cellStyle}>{agent.id}</TableCell>
                      <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>
                        <Link
                          to={`/leads-vo/admin/agents/${agent.id}`}
                          style={{ color: '#262626', textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                          {agent.organization_name ?? `Organisation #${agent.organization_id}`}
                        </Link>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Typography variant="body2" sx={{ fontSize: 12, color: '#464646', fontFamily: 'monospace' }}>
                          {agent.leads_vo_agent_id ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Chip
                          label={agent.status}
                          size="small"
                          sx={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.color,
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Chip
                          label={agent.google_calendar_connected ? 'Connecté' : 'Non connecté'}
                          size="small"
                          variant={agent.google_calendar_connected ? 'filled' : 'outlined'}
                          sx={{
                            fontSize: 12,
                            ...(agent.google_calendar_connected
                              ? { backgroundColor: '#E8F5E9', color: '#2E7D32' }
                              : { borderColor: '#BDBDBD', color: '#757575' }),
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellStyle} align="center">
                        <Switch
                          checked={agent.leads_vo_enabled}
                          onChange={() => toggleEnabled.mutate(agent.id)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FDBB5F' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {new Date(agent.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#464646' }}>
                    Aucun agent enregistré — cliquez sur "Inviter une agence" pour commencer
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setError(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>
          Inviter une agence à RDV Qualifiés
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#464646', mb: 2 }}>
            Recherchez l'agence à activer. Elle verra le menu RDV Qualifiés et pourra compléter son onboarding.
          </Typography>
          <Autocomplete
            options={availableOrgs}
            getOptionLabel={(option: any) => `${option.name} (ID: ${option.id})`}
            value={selectedOrg}
            onChange={(_, val) => setSelectedOrg(val)}
            renderInput={(params) => <TextField {...params} label="Rechercher une agence" autoFocus />}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter((o: any) =>
                o.name?.toLowerCase().includes(q) || String(o.id).includes(q)
              ).slice(0, 20);
            }}
            noOptionsText="Aucune agence trouvée"
            sx={{ mt: 1 }}
          />
          {selectedOrg && (
            <Paper sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#262626' }}>
                <strong>{selectedOrg.name}</strong> — ID Pipedrive : {selectedOrg.id}
              </Typography>
            </Paper>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDialogOpen(false); setError(''); setSelectedOrg(null); }}
            sx={{ color: '#464646', textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!selectedOrg || createAgent.isPending}
            sx={{
              backgroundColor: '#FDBB5F',
              color: '#262626',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#fcae4d' },
            }}
          >
            {createAgent.isPending ? 'Activation...' : 'Activer RDV Qualifiés'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAgents;
